import type { ActionName, ResourcePath, Role } from '@occ/contracts';
import { ACTIONS, isActionName } from '@occ/contracts';
import type { AccessControlProvider, CanParams, CanReturnType } from '@refinedev/core';
import {
  branchResourcePath,
  clientResourcePath,
  nodeResourcePath,
  tenantResourcePath,
} from '../hierarchy/index-store';
import { getCurrentUser } from './identity';
import { authorize, hasRole } from './rbac';

/**
 * `accessControlProvider` de Refine conectado al RBAC real.
 *
 * Dos caminos, según de dónde venga la pregunta:
 *
 * 1. **Botón de acción** — `can({ resource: 'actions', action: 'script.run' })`.
 *    Se resuelve contra el catálogo de `@occ/contracts`, la misma tabla que usa
 *    el backend para autorizar y el servidor MCP para publicar herramientas (P3).
 * 2. **CRUD de la jerarquía** — `can({ resource: 'clients', action: 'edit' })`.
 *    El catálogo no cubre la administración, así que se exige rol mínimo por
 *    tipo de operación en la tabla de abajo.
 *
 * En ambos casos esto solo decide qué se pinta. Quien autoriza es el backend.
 */

const DENY = (reason: string): CanReturnType => ({ can: false, reason });
const ALLOW: CanReturnType = { can: true };

/** Acciones del catálogo equivalentes a las operaciones de lectura de Refine. */
const READ_ACTIONS: Record<string, ActionName> = {
  'nodes:list': 'nodes.list',
  'nodes:show': 'nodes.get',
};

/**
 * Rol mínimo para administrar la jerarquía. Cambiar la estructura (crear o
 * borrar clientes y sucursales) es administración del tenant; renombrar un nodo
 * o moverlo de sucursal lo hace quien opera a diario.
 */
const ADMIN_REQUIREMENTS: Record<string, Role> = {
  'clients:create': 'tenant_admin',
  'clients:edit': 'tenant_admin',
  'clients:delete': 'tenant_admin',
  'branches:create': 'tenant_admin',
  'branches:edit': 'tenant_admin',
  'branches:delete': 'tenant_admin',
  'nodes:edit': 'support_technician',
  'enrollment:create': 'tenant_admin',
  // Revocar es el mismo rol que emitir, y hace falta declararlo: sin esta línea
  // la clave caería en la lectura genérica y un `read_only` vería el botón
  // encendido para recibir un 403 al pulsarlo.
  'enrollment:delete': 'tenant_admin',

  // Usuarios y permisos: conceder alcance es la operación más sensible del
  // panel, porque es la única que cambia lo que otros pueden hacer. Incluida la
  // lectura: la lista de usuarios y sus alcances es un mapa de la organización.
  'users:list': 'tenant_admin',
  'users:create': 'tenant_admin',
  'users:edit': 'tenant_admin',
  'users:delete': 'tenant_admin',
  'permissions:create': 'tenant_admin',
  'permissions:delete': 'tenant_admin',

  // El branding es del tenant, no de quien opera nodos.
  'branding:edit': 'tenant_admin',

  // Plugins (M6.1): administrarlos —crearlos, editarlos, habilitarlos por
  // cliente— es tenant_admin, igual que las reglas de alerta. Correrlos ya
  // habilitados pasa por `script.run` del catálogo, con su propio minRole.
  'plugins:edit': 'tenant_admin',

  // Canales de notificación (M12/M15): quien puede crear uno puede redirigir
  // las alertas de todo el tenant a un Slack o Discord que él controla — misma
  // exigencia que branding o usuarios, no la de un técnico de guardia.
  'notification-channels:edit': 'tenant_admin',

  // Programador de tareas (M14): puede ejecutar código en una flota entera,
  // igual que plugins y reglas de alerta.
  'scheduled-tasks:edit': 'tenant_admin',

  // Interruptor de aviso por nodo (M15 §4): acotado a un nodo que el actor ya
  // alcanza, no toca la configuración general de alertas — por eso el rol es
  // el mismo que edita el propio nodo, no el de administrar canales o reglas.
  'node-notify:edit': 'support_technician',

  // La bitácora la ve `read_only` dentro de su alcance: es lo que dice el rol en
  // `@occ/contracts` («ve inventario, salud y bitácora»).
  'audit:list': 'read_only',

  // Ver quién está trabajando es lectura; echar a alguien de una sesión, no.
  'sessions:list': 'read_only',
  'sessions:delete': 'support_technician',
};

/**
 * Reconstruye la cadena de pertenencia del recurso que se está evaluando.
 *
 * El id suelto no dice de qué es: para una acción del catálogo se interpreta
 * según el ámbito que declara la propia acción, y para un recurso de Refine
 * según su nombre. Sin esto, un permiso con alcance de cliente nunca cubriría
 * un nodo, porque la ruta llegaría con el tenant y nada más.
 */
const resolvePath = (
  params: CanParams['params'],
  resource: string | undefined,
  action: string,
): ResourcePath | null => {
  const explicit = params?.path as ResourcePath | undefined;
  if (explicit) return explicit;

  const id = params?.id === undefined ? undefined : String(params.id);
  if (!id) return tenantResourcePath();

  if (isActionName(action)) {
    return ACTIONS[action].scope === 'node' ? nodeResourcePath(id) : tenantResourcePath();
  }

  switch (resource) {
    case 'nodes':
      return nodeResourcePath(id);
    case 'clients':
      return clientResourcePath(id);
    case 'branches':
      return branchResourcePath(id, params?.clientId as string | undefined);
    default:
      return tenantResourcePath();
  }
};

export const accessControlProvider: AccessControlProvider = {
  async can({ resource, action, params }): Promise<CanReturnType> {
    const user = await getCurrentUser();
    if (!user) return DENY('Sesión no iniciada');

    const path = resolvePath(params, resource, action) ?? { tenantId: user.tenantId };
    const actor = { tenantId: user.tenantId, permissions: user.permissions };

    // 1. Acción del catálogo, invocada por su nombre.
    if (isActionName(action)) {
      const decision = authorize(actor, action, path);
      return decision.allowed ? ALLOW : DENY(decision.reason);
    }

    const key = `${resource ?? ''}:${action}`;

    // 2. Lectura de un recurso que sí tiene acción equivalente en el catálogo.
    const readAction = READ_ACTIONS[key];
    if (readAction) {
      const decision = authorize(actor, readAction, path);
      return decision.allowed ? ALLOW : DENY(decision.reason);
    }

    // 3. Administración de la jerarquía.
    const required = ADMIN_REQUIREMENTS[key];
    if (required) {
      return hasRole(user.permissions, path, required)
        ? ALLOW
        : DENY(`Requiere el rol ${required} sobre este ámbito`);
    }

    // Lectura genérica: basta con estar dentro del alcance.
    return hasRole(user.permissions, path, 'read_only')
      ? ALLOW
      : DENY('El recurso está fuera del alcance del usuario');
  },
  options: {
    buttons: { enableAccessControl: true, hideIfUnauthorized: false },
  },
};
